import { useRef } from 'react';

import {
  PaymentMethodCreateParams,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import {
  ESdkEvents,
  EventParams,
  CardDetails,
  EPaymentProvider,
  EBEPaymentMethodsName,
  StripeFinalizePaymentResponse,
} from '../../../contracts';
import { useBlikForm } from './use-blik-form';
import { sendFEEvent, sendInternalFEEvent } from '../../../eventUtil';
import { useOrderPolling } from './use-order-polling';
import { usePaymentState } from './use-payment-state';
import { useAPI } from '../../../services/api.service';
import { useCheckout } from '../../../hooks/use-checkout';
import { CoralogixLogSeverity } from '@coralogix/browser';
import { ECheckoutStatus, InternalMessageEventsName } from '../../../constants/enums';
import { logAlways, logToCoralogix } from '../../../utils/coralogix-rum';
import { getCheckoutUrl } from '../../../utils/get-checkout-url';
import { openNewTab } from '../../../utils/open-new-tab';
import { AddressElementRef } from '../components/AddressElement/hooks/use-address-element-ref';
import { generateAppchargeErrorMessage } from '../../../utils/generate-appcharge-error-message';
import { isCheckoutEmailValid } from '../../../utils/checkout-email-validation';
import { buildStripeBlikPaymentRequest, mapBlikErrorCode } from '../../../utils/blik';
import { getPaymentKey } from '../../../utils/get-payment-key';

interface useStripeSubmitParams {
  setOverlay: (value: boolean) => void;
  setApmTab?: (value: any) => void;
}

const sleep = async (ms: number) => new Promise((res) => setTimeout(res, ms));

// Stripe payment methods that complete via an external redirect (next_action.redirect_to_url),
// i.e. they require a browser tab. These need the tab opened synchronously on the click gesture
// so Safari mobile doesn't block it. Card/Link/3DS and CashApp-on-desktop are handled in-page and
// must NOT pre-open a tab. CashApp-on-mobile also redirects and is added at call time (mobile only).
const STRIPE_REDIRECT_APMS = new Set<string>([
  EBEPaymentMethodsName.ideal,
  EBEPaymentMethodsName.amazonPay,
  EBEPaymentMethodsName.eps,
  EBEPaymentMethodsName.bancontact,
  EBEPaymentMethodsName.p24,
  EBEPaymentMethodsName.blikCheckout,
  EBEPaymentMethodsName.klarna,
  EBEPaymentMethodsName.affirm,
  EBEPaymentMethodsName.afterpay,
  EBEPaymentMethodsName.clearpay,
  EBEPaymentMethodsName.krCard,
  EBEPaymentMethodsName.payco,
  EBEPaymentMethodsName.samsungPay,
  EBEPaymentMethodsName.naverPay,
  EBEPaymentMethodsName.kakaoPay,
  EBEPaymentMethodsName.upi,
]);

// the router expect to get a different display name for link
export const expressCheckoutEventToDisplayNameMapWithRouter: Record<string, string> = {
  link: 'Link',
  apple_pay: 'Apple',
  google_pay: 'Google Pay',
};

export function useStripeSubmit({ setOverlay, setApmTab }: useStripeSubmitParams) {
  const { API } = useAPI();
  const stripe = useStripe();
  const elements = useElements();
  const { showBoundary } = useErrorBoundary();
  const {
    checkoutData,
    getEventParams,
    selectedPaymentMethod,
    selectedPaymentMethodDisplayNameRef,
  } = useCheckout();
  const isStripePaymentInProgress = useRef(false);
  const apmTabRef = useRef<Window | null>(null);
  const apmRedirectIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { startOrderPolling } = useOrderPolling(handleError);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setCheckoutStatus = usePaymentState((state) => state.setCheckoutStatus);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const setIsBlikAwaitingApproval = usePaymentState((state) => state.setIsBlikAwaitingApproval);
  const fullAddress = usePaymentState((state) => state.fullAddress);
  const zipCode = usePaymentState((state) => state.zipCode);
  const shouldSaveCard = usePaymentState((state) => state.shouldSaveCard || false);
  const { triggerFormValidation, formValues, isFormValid } = useBlikForm();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  function handleError(errorMessage?: string) {
    const appChargeErrorMessage: string = generateAppchargeErrorMessage(t, errorMessage);
    setIsPaymentLoading(false);
    setIsBlikAwaitingApproval(false);
    isStripePaymentInProgress.current = false;
    setErrorMessage(appChargeErrorMessage);
    closeApmTab();
  }

  // Open our own loader page (the same /apm-dynamic-page used by the Adyen/Nuvei APM flows)
  // synchronously inside the click gesture. Safari mobile blocks window.open once the gesture's
  // user-activation has expired, which happens for APM redirects because the URL only arrives
  // after several awaits. The loader page later navigates itself to the APM URL via postMessage.
  function openApmTab() {
    const tab = openNewTab(`${getCheckoutUrl()}/apm-dynamic-page`);
    apmTabRef.current = tab;
    if (tab && setApmTab) setApmTab(tab);
  }

  // Tell the loader page to navigate itself to the APM URL. The page may still be mounting (its
  // message listener not yet attached) when we first post, and a window.open'd tab's onload event
  // is unreliable, so we resend on a short interval until the tab navigates away or we time out.
  function redirectApmTab(tab: Window, url: string) {
    clearApmRedirectInterval();
    let attempts = 0;
    const maxAttempts = 20;
    const send = () => {
      attempts += 1;
      sendInternalFEEvent(tab, InternalMessageEventsName.APM_REDIRECT, url);
      if (attempts >= maxAttempts || tab.closed) clearApmRedirectInterval();
    };
    send();
    apmRedirectIntervalRef.current = setInterval(send, 300);
  }

  function clearApmRedirectInterval() {
    if (apmRedirectIntervalRef.current) {
      clearInterval(apmRedirectIntervalRef.current);
      apmRedirectIntervalRef.current = null;
    }
  }

  function closeApmTab() {
    clearApmRedirectInterval();
    apmTabRef.current?.close();
    apmTabRef.current = null;
  }

  async function handleSubmit(
    isExpress: boolean | undefined,
    paymentMethodId: string | undefined,
    addressComponent?: AddressElementRef,
    extraParams?: Record<string, any>,
  ) {
    if (!stripe || isStripePaymentInProgress.current) return;
    isStripePaymentInProgress.current = true;
    setErrorMessage('');
    setIsPaymentLoading(true);

    // Pre-open the redirect tab now, synchronously inside the click gesture, only for APMs that
    // actually redirect (see STRIPE_REDIRECT_APMS). CashApp on mobile also redirects (to our
    // /handle-payment page) so it is included here; on desktop it is handled in-page (QR) and
    // intentionally does not open a tab. Swish always redirects to /handle-payment (all devices),
    // so it always pre-opens a tab — see handleServerResponse for why it can't run inline.
    const methodName = selectedPaymentMethod?.paymentMethodName ?? '';
    const isCashAppMobile = isMobile && methodName === EBEPaymentMethodsName.cashapp;
    const isSwish = methodName === EBEPaymentMethodsName.swish;
    if (!isExpress && (STRIPE_REDIRECT_APMS.has(methodName) || isCashAppMobile || isSwish)) {
      openApmTab();
    }

    addressComponent?.validate();
    const isZipCodeError = isExpress ? false : addressComponent && !addressComponent.isValid();
    // workaround for blik with stripe
    // ACDEV-24674: skip the BLIK branch for express checkout (Google Pay / Apple Pay / Link),
    // otherwise a prior BLIK selection persists and causes the express flow to redirect to BLIK.
    if (
      !isExpress &&
      selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.blikCheckout
    ) {
      triggerFormValidation();
      if (!isFormValid || isZipCodeError) {
        handleError();
        return;
      }
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'p24',
        billing_details: {
          email: formValues.email,
        },
        p24: {
          bank: 'blik',
        },
      });

      if (error) {
        handleError();
        return;
      }

      if (
        paymentMethod?.billing_details.email &&
        !isCheckoutEmailValid(paymentMethod.billing_details.email)
      ) {
        handleError(t('fieldErrors.invalidEmail'));
        return;
      }

      finalizePayment({
        paymentProvider: EPaymentProvider.stripe,
        receipt_email: paymentMethod?.billing_details.email,
        params: {
          paymentMethodId: paymentMethod?.id,
        },
        paymentMethodDisplayName: selectedPaymentMethod.paymentMethodDisplayName,
        paymentMethodId: selectedPaymentMethod.id,
      });
      return;
    }

    // Native BLIK (ACDEV-24843): stays on-page. The BE creates an unconfirmed
    // PaymentIntent and returns its client_secret; we confirm client-side with the
    // shopper's 6-digit code and wait for their in-app approval (no redirect).
    if (!isExpress && selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.blik) {
      triggerFormValidation();
      if (!isFormValid || isZipCodeError) {
        handleError();
        return;
      }
      await handleNativeBlikPayment();
      return;
    }

    const submitResult = await elements?.submit();
    if (submitResult?.error || isZipCodeError) {
      handleError();
      return;
    }

    const paymentMethodParams = getPaymentMethodParams(
      selectedPaymentMethod!.paymentMethodName,
      addressComponent?.values.zipCode || zipCode,
      addressComponent?.values.address || fullAddress,
    );
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements: elements!,
      params: paymentMethodParams,
    });

    if (error) {
      handleError(error.message);
      return;
    }
    // specific logic for ideal full name validation bug ACDEV-14201
    // Skip for express checkout: name comes from the wallet token (GPay/Apple Pay/Link),
    // and selectedPaymentMethod may be a stale APM-list selection (see ACDEV-24674).
    if (!isExpress && selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.ideal) {
      const fullName = paymentMethod?.billing_details.name;
      if (fullName !== null && fullName.length < 3) {
        handleError(t('fullNameIdeal'));
        return;
      }
    }

    // move logic to external function
    let cardDetails: CardDetails | undefined;
    if (paymentMethod.card) {
      cardDetails = {
        bin: '',
        nameOnCard: '',
        lastFourDigits: paymentMethod.card?.last4 || '',
        expirationMonth: paymentMethod.card?.exp_month?.toString() || '',
        expirationYear: paymentMethod.card?.exp_year?.toString() || '',
        cardBrand: paymentMethod.card?.brand,
        fingerprint: paymentMethod.card?.fingerprint || '',
        countryOfIssuance: paymentMethod.card?.country || '',
      };
    }

    const isCardPaymentMethod =
      selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.card && !isExpress;

    const receiptEmail = paymentMethod.billing_details.email || checkoutData?.customer?.email || '';

    if (receiptEmail && !isCheckoutEmailValid(receiptEmail)) {
      handleError(t('fieldErrors.invalidEmail'));
      return;
    }

    const finalizePaymentRequestBody = {
      paymentProvider: EPaymentProvider.stripe,
      receipt_email: receiptEmail,
      params: {
        paymentMethodId: paymentMethod.id,
        cardDetails,
        paymentMethodDetails: paymentMethod,
        shouldSaveCard: isCardPaymentMethod ? shouldSaveCard : undefined,
      },
      paymentMethodDisplayName: selectedPaymentMethodDisplayNameRef.current,
      paymentMethodId: paymentMethodId,
    };
    const FEeventParams: Partial<EventParams> = {
      isSavedPaymentMethodUsed: paymentMethod.type === 'link',
    };
    finalizePayment(finalizePaymentRequestBody, FEeventParams);

    // ACDEV-19298
    if (
      finalizePaymentRequestBody.paymentMethodDisplayName === 'Credit Card' &&
      (paymentMethod?.card?.wallet?.type === 'google_pay' || isExpress)
    ) {
      logAlways('Google Pay finalize Credit Card', {
        isExpress,
        extraParams: JSON.stringify(extraParams),
        checkoutData: JSON.stringify(checkoutData),
        selectedPaymentMethod: JSON.stringify(selectedPaymentMethod),
        finalizePaymentRequestBody: JSON.stringify(finalizePaymentRequestBody),
      });
    }
  }

  async function handleNativeBlikPayment() {
    const code = formValues.code?.trim();
    if (!stripe || !code || !selectedPaymentMethod) {
      handleError();
      return;
    }

    const receiptEmail = formValues.email || checkoutData?.customer?.email || '';

    let response: StripeFinalizePaymentResponse;
    try {
      response = await API.finalizePayment<StripeFinalizePaymentResponse>(
        buildStripeBlikPaymentRequest(selectedPaymentMethod, receiptEmail),
      );
    } catch (error: any) {
      const errResponse = error?.response?.data;
      setIsPaymentLoading(false);
      showBoundary(t('unableToProcessPayment'));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: errResponse?.message,
        error: { isFatal: true, reason: errResponse?.message },
      });
      return;
    }

    // No client_secret means the BE didn't create the unconfirmed intent (e.g. finalize
    // already failed); surface its mapped error instead of attempting a confirm.
    if (!response?.stripe?.clientSecret) {
      handleError(response?.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: response?.stripe?.message || '',
      });
      return;
    }

    setIsBlikAwaitingApproval(true);
    try {
      const { paymentIntent, error } = await stripe.confirmBlikPayment(response.stripe.clientSecret, {
        payment_method: {
          blik: {},
          ...(receiptEmail ? { billing_details: { email: receiptEmail } } : {}),
        },
        payment_method_options: { blik: { code } },
      });

      if (error) {
        handleError(mapBlikErrorCode(error.code ?? error.payment_intent?.last_payment_error?.code));
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          reason: error.message || '',
        });
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, { ...getEventParams() });
        startOrderPolling(response.orderId);
        return;
      }

      handleError(mapBlikErrorCode(paymentIntent?.last_payment_error?.code));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: paymentIntent?.last_payment_error?.message || '',
      });
    } catch (confirmError: any) {
      // A thrown confirm (network / Stripe.js failure) would otherwise skip the reset in
      // `finally` via an early return path; treat it like a decline so the authorization
      // overlay never gets stuck.
      handleError();
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: confirmError?.message || '',
      });
    } finally {
      setIsBlikAwaitingApproval(false);
    }
  }

  async function finalizePayment(body: any, FEeventParams?: Partial<EventParams>) {
    try {
      const data = await API.finalizePayment<StripeFinalizePaymentResponse>(body);
      handleServerResponse(data, FEeventParams);
    } catch (error: any) {
      const errResponse = error?.response?.data;
      setIsPaymentLoading(false);
      closeApmTab();
      showBoundary(t('unableToProcessPayment'));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: errResponse.message,
        error: {
          isFatal: true,
          reason: errResponse.message,
        },
      });
    }
  }

  async function handleServerResponse(
    response: StripeFinalizePaymentResponse,
    FEeventParams?: Partial<EventParams>,
  ) {
    if (response.stripe.status === 'succeeded') {
      closeApmTab();
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      startOrderPolling(response.orderId, FEeventParams);
    } else if (response.stripe.status === 'failed') {
      handleError(response.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: response.stripe.message || '',
      });
    } else if (response.stripe.status === 'redirect') {
      // get the PaymentIntent object from stripe to check if there is a redirect URL
      const paymentIntentResult = await stripe?.retrievePaymentIntent(response.stripe.clientSecret);
      let url = paymentIntentResult?.paymentIntent?.next_action?.redirect_to_url?.url;

      // CashApp on mobile and Swish on all devices resolve their next action via an app deep link /
      // QR that Stripe would otherwise handle inline. Running handleNextAction inline navigates the
      // checkout iframe itself (breaking the flow), so route them through the standalone
      // /handle-payment page — Stripe's own (localized) handleNextAction UI running at top level —
      // opened in the pre-opened tab instead. Swish is device-agnostic because its next_action
      // always exposes both a QR and an app deep link together (and Stripe's own device detection,
      // which we can't override, decides which to use), so inline is never iframe-safe for it.
      const methodName = selectedPaymentMethod?.paymentMethodName;
      const isCashAppMobile = isMobile && methodName === EBEPaymentMethodsName.cashapp;
      const isSwish = methodName === EBEPaymentMethodsName.swish;
      if (isCashAppMobile || isSwish) {
        const checkoutUrl = getCheckoutUrl();
        const stripePk = getPaymentKey(selectedPaymentMethod?.pspSubMidPk, checkoutData?.pks.stripe, {
          provider: EPaymentProvider.stripe,
          paymentMethod: methodName ?? '',
        });
        url = `${checkoutUrl}/handle-payment/${response.stripe.clientSecret}/${stripePk}`;
      }
      if (url) {
        handleRedirectAPM(url, response);
      } else {
        // no redirect URL: stripe handles the next action in-page (e.g. 3ds, cashapp on desktop),
        // so a pre-opened tab (if any) is not needed.
        closeApmTab();
        handlePaymentNextAction(response.stripe.clientSecret, response.orderId, FEeventParams);
      }
    }
  }

  async function handleRedirectAPM(url: string, response: any) {
    setOverlay(true);
    let retries = 2;
    // For APMs we pre-opened a loader tab for (STRIPE_REDIRECT_APMS / CashApp mobile), reuse it and
    // let the page navigate itself to the APM URL via postMessage — Safari mobile blocks a late
    // window.open. For any other method (or if the gesture-time open was blocked), fall back to
    // opening the tab here, exactly as before, so no flow regresses.
    let newtab = apmTabRef.current;
    if (newtab) {
      redirectApmTab(newtab, url);
    } else {
      newtab = window.open(url as string, '_blank');
      apmTabRef.current = newtab;
      if (newtab && setApmTab) setApmTab(newtab);
    }
    if (!newtab) {
      // Popup was blocked (popup blocker, sandboxed iframe without allow-popups, mobile gesture
      // lost, etc.). Without a tab the user can never complete the APM authorization, and polling
      // would never exit -> the UI would stay stuck behind the overlay.
      setOverlay(false);
      const reason = 'APM redirect popup blocked';
      logToCoralogix(CoralogixLogSeverity.Warn, reason, {
        url,
        paymentMethodName: selectedPaymentMethod?.paymentMethodName,
      });
      handleError();
      return;
    }
    // Snapshot the abort signal so a click on the overlay's "try again" button
    // (which increments it via usePaymentState.abortApmFlow) reliably terminates
    // this loop, even when `newtab.closed` is unreliable under COOP / sandboxed
    // iframes / cross-origin popups.
    const abortSnapshot = usePaymentState.getState().apmAbortSignal;
    let polling = true;
    while (polling) {
      await sleep(1000);
      if (usePaymentState.getState().apmAbortSignal !== abortSnapshot) {
        // User explicitly asked to abort -> tear down silently.
        // abortApmFlow already cleared isPaymentLoading and the overlay flag,
        // so don't call handleError (which would show a misleading generic
        // error after a user-initiated retry).
        polling = false;
        closeApmTab();
        setOverlay(false);
        window.parent.focus();
        isStripePaymentInProgress.current = false;
        return;
      }
      const paymentIntentResults = await stripe?.retrievePaymentIntent(
        response.stripe.clientSecret,
      );
      if (paymentIntentResults?.paymentIntent?.status === 'processing') {
        API.log({
          component: 'useSubmitPayment => handleRedirectAPM',
          params: { response, paymentIntentResults },
        });
      }
      if (paymentIntentResults?.paymentIntent?.status === 'succeeded') {
        // payment intent success
        polling = false;
        closeApmTab();
        setOverlay(false);
        window.parent.focus();
        setCheckoutStatus(ECheckoutStatus.PENDING);
        startOrderPolling(response.orderId);
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
          ...getEventParams(),
        });
      } else if (paymentIntentResults?.paymentIntent?.status === 'requires_payment_method') {
        // payment intent failed
        polling = false;
        closeApmTab();
        setOverlay(false);
        window.parent.focus();
        const errMessage = paymentIntentResults.paymentIntent.last_payment_error?.message;
        handleError(errMessage);
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          reason: errMessage,
        });
      } else if (paymentIntentResults?.error) {
        if (paymentIntentResults.error.code === 'lock_timeout' && retries > 0) {
          retries--;
        } else {
          // payment intent error
          polling = false;
          closeApmTab();
          setOverlay(false);
          window.parent.focus();
          const errMessage = paymentIntentResults.error.message;
          handleError(errMessage);
          sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
            ...getEventParams(),
            reason: errMessage,
          });
        }
      } else if (newtab?.closed) {
        setOverlay(false);
        window.parent.focus();
        polling = false;
        handleError(t('yourPaymentWasDeclined'));
      }
    }
  }

  async function handlePaymentNextAction(
    clientSecret: string,
    orderId: string,
    FEeventParams?: Partial<EventParams>,
  ) {
    const paymentIntentResult = await stripe?.handleNextAction({
      clientSecret,
    });

    if (paymentIntentResult?.paymentIntent?.status === 'processing') {
      API.log({
        component: 'useSubmitPayment => handlePaymentNextAction',
        params: { paymentIntentResult },
      });
    }

    if (paymentIntentResult?.error) {
      const errorMessage = paymentIntentResult.error.message || '';
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: errorMessage,
      });
      handleError(errorMessage);
    } else if (
      paymentIntentResult?.paymentIntent?.status === 'requires_payment_method' ||
      paymentIntentResult?.paymentIntent?.status === 'requires_action'
    ) {
      const errorMessage = paymentIntentResult?.paymentIntent?.last_payment_error?.message || '';
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: errorMessage,
      });
      handleError(errorMessage);
    } else {
      // actions handled successfully
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      startOrderPolling(orderId, FEeventParams);
    }
  }

  function getPaymentMethodParams(
    paymentMethodName: string,
    zipCode?: string,
    fullAddress?: string,
  ) {
    const cardParams: PaymentMethodCreateParams = {
      billing_details: {
        address: {
          postal_code: zipCode,
          line1: fullAddress,
          country: checkoutData?.customer.geoLocator.countryCode2,
        },
      },
    };
    const apmParams: PaymentMethodCreateParams = {
      billing_details: {
        address: {
          country: checkoutData?.customer.geoLocator.countryCode2,
        },
      },
    };
    const affirmAndAfterpayParams: PaymentMethodCreateParams = {};
    switch (paymentMethodName) {
      case EBEPaymentMethodsName.card:
        return cardParams;
      case EBEPaymentMethodsName.afterpay:
      case EBEPaymentMethodsName.affirm:
        return affirmAndAfterpayParams;
      default:
        return apmParams;
    }
  }

  const onExpressElementConfirm = (event: StripeExpressCheckoutElementConfirmEvent) => {
    const displayName = expressCheckoutEventToDisplayNameMapWithRouter[event.expressPaymentType];
    selectedPaymentMethodDisplayNameRef.current = displayName;
    const paymentMethod = checkoutData?.paymentMethods.find(
      (pm) =>
        pm.paymentMethodDisplayName === displayName && pm.provider === EPaymentProvider.stripe,
    );
    handleSubmit(true, paymentMethod?.id, undefined, {
      displayName,
      expressPaymentType: event.expressPaymentType,
    });
  };

  return {
    handleStripeSubmit: handleSubmit,
    onExpressElementConfirm,
  };
}
